import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "465",
  "slug": "largest-volcanoes-in-the-world-ranked",
  "title": "Largest Volcanoes in the World, Ranked",
  "excerpt": "Largest means different things for volcanoes: by volume, by footprint, or by caldera. Here are the giants that top each list, from Mauna Loa to the submarine Tamu Massif.",
  "date": "2026-06-17",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1619266465172-02a857c3556d?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx2b2xjYW5vJTIwbGFuZHNjYXBlfGVufDF8MHx8fDE3ODE2ODQyMjR8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "By volume above the seafloor, Mauna Loa in Hawaii is the largest active volcano on Earth. By sheer footprint, the submarine Tamu Massif may be even bigger, while Toba and Yellowstone hold the record for the largest caldera-forming eruptions.",
  "content": [
    {
      "type": "paragraph",
      "text": "Ranking the largest volcanoes in the world sounds simple until you ask what largest actually means. The tallest? The one with the most material? The widest footprint? The biggest eruption ever recorded? Each question points to a different giant. Here is how the contenders stack up across the ways geologists measure volcanic size."
    },
    {
      "type": "heading",
      "text": "What Does Largest Even Mean?"
    },
    {
      "type": "paragraph",
      "text": "Volcanoes can be compared in several ways, and they rarely agree on a single champion:"
    },
    {
      "type": "list",
      "items": [
        "By volume, the total amount of rock and lava the volcano is built from.",
        "By footprint, the area the volcano covers at its base.",
        "By height, measured from base to summit or above sea level.",
        "By eruption size, the volume of material thrown out in a single event."
      ]
    },
    {
      "type": "paragraph",
      "text": "Keep those categories in mind, because the answer to which volcano is biggest depends entirely on which one you choose. A volcano can win one contest by a mile and barely place in another."
    },
    {
      "type": "heading",
      "text": "Mauna Loa: The Volume Champion"
    },
    {
      "type": "paragraph",
      "text": "By volume above the seafloor, Mauna Loa in Hawaii is widely regarded as the largest active volcano on Earth. It is a shield volcano, built from countless thin, runny lava flows that spread out into a broad, gently sloping dome. Although it does not look dramatic from a distance, Mauna Loa is staggeringly massive: most of its bulk lies hidden beneath the ocean, and the island it helps build is so heavy that it presses down on the seafloor beneath it. The gentle slopes are the giveaway that you are looking at a true giant rather than a steep, classic cone."
    },
    {
      "type": "heading",
      "text": "Tamu Massif: The Footprint Giant"
    },
    {
      "type": "paragraph",
      "text": "Far out in the northwest Pacific, east of Japan and deep beneath the waves, lies Tamu Massif, a submarine volcanic structure with an enormous footprint, spanning an area comparable to some entire countries. When it was first studied in detail, researchers suggested it might be the single largest volcano on Earth by area. Later research has questioned whether it behaves like one classic volcano or more like a stretch of mid-ocean ridge, so its exact status is still debated. Either way, it is a reminder that the biggest volcanic features on the planet are often hidden far below the waves, out of sight."
    },
    {
      "type": "heading",
      "text": "Supervolcanoes: Toba and Yellowstone"
    },
    {
      "type": "paragraph",
      "text": "If the measure is the size of a single eruption, the record belongs to supervolcanoes. Lake Toba in Indonesia is the site of one of the largest eruptions of the last million years, leaving behind a caldera now filled by a huge lake. Yellowstone in the United States sits over a volcanic system so vast that its past eruptions blanketed much of a continent in ash. These giants do not build tall, classic cones; instead they leave broad calderas that can be so large they are hard to recognize from ground level at all."
    },
    {
      "type": "heading",
      "text": "Honorable Mentions"
    },
    {
      "type": "list",
      "items": [
        "Ojos del Salado, on the Chile and Argentina border, is the highest volcano in the world, towering near the top of the Andes.",
        "Mount Etna in Sicily is the largest and most active volcano in Europe.",
        "Mauna Kea, Mauna Loa's neighbor, is taller than Mount Everest when measured from its base on the seafloor.",
        "Mount Kilimanjaro in Tanzania is the highest volcano in Africa and the continent's tallest peak."
      ]
    },
    {
      "type": "heading",
      "text": "Why Hawaii Builds Giants"
    },
    {
      "type": "paragraph",
      "text": "It is no accident that several of the heavyweights sit in Hawaii. They are shield volcanoes, fed by very fluid lava that flows a long way before it cools. Instead of piling up into a steep cone, that runny lava spreads out layer upon layer into a vast, low dome. Over hundreds of thousands of years, and with a steady supply of magma from a hot spot deep in the mantle, the result is some of the largest single mountains anywhere on the planet, even if they never look especially tall."
    }
  ]
};
